import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Form,
  Grid,
  Input,
  Pagination,
  Result,
  Row,
  Select,
  Spin,
} from 'antd';
import NewsList from 'views/News/NewsList';
import { useDispatch, useSelector } from 'react-redux';
import { selectNoticias, selectTiposDeNoticias, setNoticias } from 'store/app';
import { COLORS, Text, Title } from 'components';
import { Link, useNavigate } from 'react-router-dom';
import { toMoment } from 'utils/normalizers';
import useQuery from 'hooks/useQuery';
import { getNoticias } from 'api/catalogos';
import { DATE_FORMAT } from 'utils/formatters';
import { NEWS_ARCHIVE_DATE } from 'utils/env';

const beforeString = `
::before {
  position: absolute;
  content: '';
  height: 18px;
  top: 6px;
  left: -2px;
  border-left: 3px solid ${COLORS.accent};
}`;

const from = toMoment(NEWS_ARCHIVE_DATE);
const today = toMoment(new Date());
const paramsKeys = ['palabra_clave', 'tipo_noticia', 'fecha_publicacion', 'page'];

const diff = today.diff(from, 'months');
const months = (new Array(diff || 0)).fill({}).map((_, i) => {
  const d = from.clone().add(i + 1, 'months');
  return {
    value: d.format('YYYY-MM'),
    label: d.format('MMMM YYYY'),
  };
});

const News = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const screens = Grid.useBreakpoint();
  const query = useQuery(paramsKeys, (e, i) => ([1, 3].includes(i) && e ? parseInt(e, 10) : e));
  const tiposDeNoticias = useSelector(selectTiposDeNoticias);
  const noticias = useSelector(selectNoticias);
  const [loading, setLoading] = useState(true);

  const fetchNoticias = async (page = 1) => {
    const params = { page };
    paramsKeys.forEach((k, i) => {
      if (query[i]) {
        if (i === 2) {
          const d = toMoment(query[i]);
          params[`${k}_hasta`] = d.clone().endOf('month').format(DATE_FORMAT);
          params[`${k}_desde`] = d.clone().startOf('month').format(DATE_FORMAT);
          return;
        }
        params[k] = query[i];
      }
    });
    if (JSON.stringify(params) === JSON.stringify(noticias.params)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    await getNoticias(params, dispatch);
    setLoading(false);
  };

  useEffect(() => { fetchNoticias(); }, [query]);

  useEffect(() => () => dispatch(setNoticias({ results: [] })), []);

  const composeLink = (idx, value) => {
    const _query = [...query];
    _query[idx] = value;
    const queryString = _query.map((q, i) => {
      if (!q) return '';
      return `${paramsKeys[i]}=${q}`;
    }).filter((e) => e).join('&');
    return `?${queryString}`;
  };

  return (
    <Spin spinning={loading}>
      <Row>
        <Col xs={24} sm={24} md={18} lg={19} order={screens.md ? 1 : 2}>
          {!!noticias.results.length && (
            <>
              <NewsList />
              <Pagination
                current={noticias.params?.page}
                onChange={(page) => navigate(composeLink(3, page))}
                total={noticias.count}
              />
            </>
          )}
          {!loading && Object.keys(noticias.params || {}).length > 1
            && !noticias.results.length && <Result title="No se encontraron noticias relacionadas" />}
          {!loading && Object.keys(noticias.params || {}).length <= 1
            && !noticias.results.length && <Result title="No hay noticias disponibles" />}
        </Col>
        <Col
          xs={24}
          sm={24}
          md={6}
          lg={5}
          order={screens.md ? 2 : 1}
          style={{ paddingLeft: screens.md ? 20 : 0 }}
        >
          {screens.md ? (
            <Row gutter={[10, 20]}>
              <Col span={24}>
                <Input.Search
                  size="large"
                  placeholder={screens.xl ? 'Búsqueda por título' : 'Título'}
                  onSearch={(_titulo) => navigate(composeLink(0, _titulo))}
                />
              </Col>
              <Col span={24}>
                <Title level={3}>Categorías</Title>
                {tiposDeNoticias.map((cat) => {
                  const isSelected = query[1] === cat.value;
                  return (
                    <Button type="link" block style={{ padding: '0 0 0 6px', textAlign: 'left' }} key={cat.value}>
                      <Link to={composeLink(1, !isSelected ? cat.value : null)}>
                        <Text
                          before={isSelected ? beforeString : ''}
                          $accent={isSelected}
                          strong={isSelected}
                        >
                          {cat.label}
                        </Text>
                      </Link>
                    </Button>
                  );
                })}
              </Col>
              {!!months.length && (
                <Col span={24}>
                  <Title level={3}>Archivo</Title>
                  {months.map((mon) => {
                    const isSelected = query[2] === mon.value;
                    return (
                      <Button block type="link" style={{ padding: '0 0 0 6px', textAlign: 'left' }} key={mon.value}>
                        <Link to={composeLink(2, !isSelected ? mon.value : null)}>
                          <Text
                            before={isSelected ? beforeString : ''}
                            $accent={isSelected}
                            strong={isSelected}
                          >
                            {mon.label}
                          </Text>
                        </Link>
                      </Button>
                    );
                  })}
                </Col>
              )}
            </Row>
          ) : (
            <Form layout="vertical" style={{ padding: '0 10px' }}>
              <Col span={24}>
                <Form.Item label="Título">
                  <Input.Search
                    onSearch={(_titulo) => navigate(`?palabra_clave=${_titulo}`)}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="Categorías">
                  <Select
                    options={tiposDeNoticias}
                    value={parseInt(query[1], 10) || undefined}
                    onSelect={(val) => navigate(composeLink(1, !(query[1] === val) && val))}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="Archivo">
                  <Select
                    options={months}
                    value={(query[2])}
                    onSelect={(val) => navigate(composeLink(2, !(query[2] === val) && val))}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Form>
          )}
        </Col>
      </Row>
    </Spin>
  );
};

export default News;
